/**
 * Game Operations Module
 *
 * This module contains utilities for game-related operations including:
 * - Finding game groups on canvas
 * - Calculating game group center points
 * - Redistributing game groups with horizontalSpacing and verticalSpacing
 * - Getting game group count
 * - Populating game groups with data
 * - Mapping game data to field keys
 * - Constructing game data from match schedule
 * - Handling stats groups
 */

import { ELEMENT_TYPE, STATS_KEY_OPTIONS, STATS_KEY_TO_DF_KEY_MAP } from "constants/ThumbnailEditor";
import moment from "moment";

import { findObjectByPath } from "./form-structure";
import { updateImageByUrlAsync } from "./image-operations";
import { isTextType } from "./shared-utils";
import { resetTextObjectCache, transformTextCase, updateTextByPath } from "./text-operations";

/**
 * Find all game groups on canvas or in thumbnail data
 *
 * @param {Object} canvas - Fabric canvas instance
 * @param {Array} thumbnailDataObjects - Optional thumbnail data objects
 * @returns {Array} Array of game groups with their paths and indices
 */
export const findGameGroups = (canvas, thumbnailDataObjects = null) => {
  if (!canvas && !thumbnailDataObjects) return [];

  const objects = thumbnailDataObjects || canvas.getObjects();
  const gameGroups = [];

  objects.forEach((obj, index) => {
    if (obj.type === "group" && obj.templatePayload?.key === "game") {
      gameGroups.push({
        object: obj,
        path: `objects[${index}]`,
        index,
      });
    }
  });

  return gameGroups;
};

/**
 * Calculate the center point of all game groups
 *
 * @param {Array} gameGroups - Array of game group objects
 * @returns {Object|null} Center point with centerY and left properties
 */
export const calculateGameGroupsCenter = (gameGroups) => {
  if (!gameGroups || gameGroups.length === 0) return null;

  // Calculate center Y position (middle of the group vertically)
  const centerYs = gameGroups.map((g) => {
    const obj = g.object;
    return obj.top + (obj.height * obj.scaleY) / 2;
  });

  const avgCenterY = centerYs.reduce((sum, y) => sum + y, 0) / centerYs.length;

  // Use the first group's left position for horizontal alignment
  const left = gameGroups[0].object.left;

  return {
    centerY: avgCenterY,
    left,
  };
};

/**
 * Get the current count of game groups on canvas
 *
 * @param {Object} canvas - Fabric canvas instance
 * @param {Array} thumbnailDataObjects - Optional thumbnail data objects
 * @returns {number} Count of game groups
 */
export const getGameGroupCount = (canvas, thumbnailDataObjects) => {
  const gameGroups = findGameGroups(canvas, thumbnailDataObjects);
  return gameGroups.length;
};

/**
 * Construct game data from match schedule, teams, and datafeed
 *
 * @param {Object} params - Parameters object
 * @param {Object} params.matchSchedule - Match schedule data
 * @param {Object} params.teamsMap - Map of teams by ID
 * @param {Object} params.tournamentOptions - Tournament options (logos, etc.)
 * @param {Array} params.datafeedData - Datafeed data array
 * @returns {Object|null} Constructed game data or null
 */
export const constructGameData = ({ matchSchedule, teamsMap, tournamentOptions, datafeedData }) => {
  if (!matchSchedule) return null;

  const teams = [teamsMap[matchSchedule?.teams[0]?.teamId], teamsMap[matchSchedule?.teams[1]?.teamId]];
  const matchDatafeedData = datafeedData.find((data) => data?.matchInfo?.matchId === matchSchedule?.extMatchId);

  const homeTeam = teams.find((team) => team.name === matchDatafeedData?.matchInfo?.homeContestantName) || teams[0];
  const awayTeam = teams.find((team) => team.name === matchDatafeedData?.matchInfo?.awayContestantName) || teams[1];

  const matchDate = matchDatafeedData?.matchInfo?.matchDate || matchSchedule?.matchStartTime;

  const matchTime = moment(matchDate).local().format("hh:mm A");

  const dfHomeTeam = matchDatafeedData?.teamStats?.find(
    (team) => team.teamName === matchDatafeedData?.matchInfo?.homeContestantName,
  );

  const dfAwayTeam = matchDatafeedData?.teamStats?.find(
    (team) => team.teamName === matchDatafeedData?.matchInfo?.awayContestantName,
  );

  const dfPlayer_1 = matchDatafeedData?.playerStats?.[0];

  const allPlayers = [
    ...teamsMap[matchSchedule?.teams[0]?.teamId].players,
    ...teamsMap[matchSchedule?.teams[1]?.teamId].players,
  ];

  const player_1 = allPlayers.find((player) => player.name === dfPlayer_1?.personName) || allPlayers[0];

  const team_1_coach_image =
    homeTeam?.teamImages?.teamCoachImages.find((image) =>
      image.graphicTags.find(
        (tag) => (tag.key === "Looking Direction" || tag.key === "looking_direction") && tag.value === "left",
      ),
    )?.value || homeTeam?.teamImages?.teamCoachImages[0]?.value;

  const team_2_coach_image =
    awayTeam?.teamImages?.teamCoachImages.find((image) =>
      image.graphicTags.find(
        (tag) => (tag.key === "Looking Direction" || tag.key === "looking_direction") && tag.value === "right",
      ),
    )?.value || awayTeam?.teamImages?.teamCoachImages[0]?.value;

  const keyMap = {
    home_team: homeTeam?.metaData?.translations?.en?.teamName || homeTeam?.name,
    away_team: awayTeam?.metaData?.translations?.en?.teamName || awayTeam?.name,
    team_1_logo: homeTeam?.teamImages?.teamLogos[0]?.value,
    team_2_logo: awayTeam?.teamImages?.teamLogos[0]?.value,
    home_team_score: String(dfHomeTeam?.sPoints || 0),
    away_team_score: String(dfAwayTeam?.sPoints || 0),
    match_time: matchTime,
    match_date: matchDate,
    match_venue: matchDatafeedData?.matchInfo?.venue,
    date: matchDate,
    sponsor_logo: tournamentOptions?.sponsorLogos[0]?.value,
    broadcaster_logo: tournamentOptions?.broadcasterLogos[0]?.value,
    player_1,
    player_1_stats: dfPlayer_1,
    team_1_coach_image,
    team_2_coach_image,
  };

  return keyMap;
};

/**
 * Get player name based on name type
 *
 * @param {Object} player - Player object
 * @param {string} nameType - Name type (full_name, first_name, last_name, middle_name)
 * @returns {string} Player name
 */
const getPlayerNameBasedOnNameType = (player, nameType) => {
  switch (nameType) {
    case "full_name":
      return player.name;
    case "first_name":
      return player.firstName;
    case "last_name":
      return player.lastName;
    case "middle_name":
      return player.middleName;
    default:
      return player.name;
  }
};

/**
 * Handle custom text population with styling
 *
 * @param {Object} keyMap - Data key map
 * @param {Object} field - Form field object
 * @param {Object} editorInstance - Editor instance
 * @returns {{ text: string, styles: Object }} Text and character styles
 */
const handleCustomTextPopulate = (keyMap, field, editorInstance) => {
  const object = findObjectByPath(editorInstance.canvas, field.path);
  const { templatePayload } = field;

  let index = 0;
  let finalTextParts = [];
  const characterStyles = {};
  const textCase = templatePayload?.textCase;

  const parentStyles = {
    fontFamily: object.fontFamily,
    fontStyle: object.fontStyle,
    fontSize: object.fontSize,
    fontWeight: object.fontWeight,
    fill: object.fill,
    textAlign: object.textAlign,
    fontUnderline: object.fontUnderline,
    fontLinethrough: object.fontLinethrough,
    lineHeight: object.lineHeight || 1.0,
  };

  const mergeStyles = (rangeStyles) => {
    return {
      fontFamily: rangeStyles.fontFamily ?? parentStyles.fontFamily,
      fontStyle: rangeStyles.fontStyle ?? parentStyles.fontStyle,
      fontSize: rangeStyles.fontSize ?? parentStyles.fontSize,
      fontWeight: rangeStyles.fontWeight ?? parentStyles.fontWeight,
      fill: rangeStyles.fill ?? parentStyles.fill,
      textAlign: rangeStyles.textAlign ?? parentStyles.textAlign,
      fontUnderline: rangeStyles.fontUnderline ?? parentStyles.fontUnderline,
      fontLinethrough: rangeStyles.fontLinethrough ?? parentStyles.fontLinethrough,
      lineHeight: rangeStyles.lineHeight ?? parentStyles.lineHeight,
    };
  };

  object.templatePayloadRanges.forEach((range) => {
    const { templatePayload, styles } = range;
    const key = templatePayload?.key;

    let text = "";
    if (key === "free_text") {
      text = templatePayload.value;
    } else if (key.includes("match_date")) {
      const format = templatePayload?.nameType || "DD MMM YYYY";
      if (format) {
        try {
          text = moment(keyMap?.[key]).format(format);
        } catch (error) {
          console.error("Error in handleCustomTextPopulate:", error);
          text = keyMap?.[key];
        }
      }
    } else {
      text = keyMap?.[key] || "";
    }

    const rangeTextCase = templatePayload?.textCase || textCase;
    const transformed = transformTextCase(text, rangeTextCase);
    templatePayload.value = transformed;
    finalTextParts.push(transformed);

    const mergedStyle = mergeStyles(styles || {});
    for (let i = 0; i < transformed.length; i++) {
      if (!characterStyles[0]) characterStyles[0] = {};
      characterStyles[0][index + i] = { ...mergedStyle };
    }

    index += transformed.length;
  });

  const finalText = finalTextParts.join("");

  return { text: finalText, styles: characterStyles };
};

/**
 * Map game data properties to field keys
 *
 * @param {Object} game - Game data object
 * @param {Object} field - Form field object
 * @param {Object} editorInstance - Editor instance
 * @returns {{ value: *, styles: Object|null }} Mapped value and styles or null
 */
export const mapGameDataToFieldKey = (game, field, editorInstance) => {
  const { templatePayload } = field;
  const fieldKey = templatePayload?.key;

  if (!game || !fieldKey) return null;

  const type = templatePayload?.type;

  let value = game[fieldKey] || null;
  let styles = null;

  if (type === ELEMENT_TYPE.TEXT) {
    if (templatePayload?.key === "custom_text") {
      const { text, styles: stylesData } = handleCustomTextPopulate(game, field, editorInstance);
      value = text;
      styles = stylesData;
    } else {
      value = transformTextCase(value, templatePayload?.textCase);
    }
  }

  return { value, styles };
};

/**
 * Populate a stats group with player stats data
 *
 * @param {Object} editorInstance - Editor instance
 * @param {Object} field - Form field object
 * @param {Object} gameData - Game data containing player stats
 */
export const populateStatsGroup = (editorInstance, field, gameData, replaceStatsKey = true) => {
  const { player_1_stats, player_1 } = gameData;

  let indexCounter = 0;

  const object = findObjectByPath(editorInstance.canvas, field.path);

  object.set({
    groupValue: player_1.name,
  });

  field.children.forEach((child) => {
    const templatePayload = child.templatePayload;

    if (templatePayload.key === "player_1_name") {
      const value = transformTextCase(
        getPlayerNameBasedOnNameType(player_1, templatePayload.nameType),
        templatePayload.textCase,
      );
      updateTextByPath({ editorInstance, path: child.path, newText: value, populateGroup: true });
    } else if (templatePayload.key === "stats_item") {
      const statsItemChildren = child.children;

      const statsKeyField = statsItemChildren.find(
        (statsItemChild) => statsItemChild.templatePayload.key === "stats_key",
      );

      const statsValueField = statsItemChildren.find(
        (statsItemChild) => statsItemChild.templatePayload.key === "stats_value",
      );

      let statsKey = "";
      if (!replaceStatsKey) {
        const statsKeyObj = findObjectByPath(editorInstance.canvas, statsKeyField.path);
        statsKey = statsKeyObj.text;
      } else {
        statsKey = STATS_KEY_OPTIONS?.[indexCounter]?.value || "";
      }

      const statsKeyDF = STATS_KEY_TO_DF_KEY_MAP?.[statsKey] || "";
      const statsValue = player_1_stats?.[statsKeyDF] || "";

      if (statsKey) {
        updateTextByPath({
          editorInstance,
          path: statsKeyField.path,
          newText: transformTextCase(statsKey, statsKeyField.templatePayload.textCase),
          populateGroup: true,
        });
      }

      if (statsValue) {
        updateTextByPath({
          editorInstance,
          path: statsValueField.path,
          newText: transformTextCase(statsValue, statsValueField.templatePayload.textCase),
          populateGroup: true,
        });
      }
      indexCounter++;
    }
  });
};

/**
 * Recursively find all fields within a group and populate with game data (Async version)
 *
 * @param {Object} editorInstance - Editor instance
 * @param {Array} formFields - Form structure array
 * @param {string} gameGroupPath - Path to the game group
 * @param {Object} gameData - Game data to populate
 * @returns {Promise<void>}
 */
export const populateGameGroupWithDataAsync = async (editorInstance, formFields, gameGroupPath, gameData) => {
  if (!editorInstance || !formFields || !gameGroupPath || !gameData) return;

  // Find the game group in form structure
  const findFieldByPath = (fields, targetPath) => {
    for (const field of fields) {
      if (field.path === targetPath) return field;
      if (field.children) {
        const found = findFieldByPath(field.children, targetPath);
        if (found) return found;
      }
    }
    return null;
  };

  const gameGroup = findFieldByPath(formFields, gameGroupPath);
  if (!gameGroup || !gameGroup.children) {
    console.warn("⚠️ Game group not found or has no children:", gameGroupPath);
    return;
  }

  // Recursively populate all fields in the group
  const populateFields = async (fields) => {
    const promises = [];

    fields.forEach((field) => {
      const fieldKey = field.templatePayload?.key;
      const { value, styles } = mapGameDataToFieldKey(gameData, field, editorInstance);

      if (fieldKey === "stats_group") {
        promises.push(populateStatsGroup(editorInstance, field, gameData));
      } else if (field.type === "group" && field.children) {
        // Recursively populate nested groups (like free_group)
        promises.push(populateFields(field.children));
      } else if (isTextType(field.type) && fieldKey) {
        // Populate text fields (synchronous, but wrapped in Promise for consistency)
        if (value !== null) {
          updateTextByPath({
            editorInstance,
            path: field.path,
            newText: value,
            styles,
            isCustomText: field.templatePayload?.key === "custom_text",
            populateGroup: true,
          });
        }
      } else if (field.type === "image" && fieldKey) {
        // Populate image fields (asynchronous - wait for load)
        if (value) {
          promises.push(updateImageByUrlAsync(editorInstance, field.path, value));
        }
      }
    });

    // Wait for all operations to complete
    await Promise.all(promises);
  };

  await populateFields(gameGroup.children);

  // Force group update after all images are loaded
  // This is crucial: it refreshes the group's internal state and forces re-render
  const groupObject = findObjectByPath(editorInstance.canvas, gameGroupPath);
  if (groupObject && groupObject.type === "group") {
    // Store original position
    const originalLeft = groupObject.left;
    const originalTop = groupObject.top;

    // addWithUpdate recalculates group dimensions and forces Fabric to update internal elements
    groupObject.addWithUpdate();

    // Reset cached values after group dimensions change
    // This ensures vertical centering and font sizing use fresh baseline on next text update
    resetTextObjectCache(groupObject);

    // Restore original position (addWithUpdate might shift it)
    groupObject.set({
      left: originalLeft,
      top: originalTop,
    });

    groupObject.setCoords();
    editorInstance.canvas.requestRenderAll();
  }
};

/**
 * Legacy sync version for backward compatibility
 * @deprecated Use populateGameGroupWithDataAsync instead
 */
export const populateGameGroupWithData = (editorInstance, formFields, gameGroupPath, gameData) => {
  populateGameGroupWithDataAsync(editorInstance, formFields, gameGroupPath, gameData).catch((error) => {
    console.error("Error in populateGameGroupWithData:", error);
  });
};

/**
 * Populate multiple game groups without redistribution or cloning
 * Groups are filled based on:
 * 1. Game selection order
 * 2. Group canvas position (top to bottom)
 *
 * @param {Object} params - Parameters object
 * @param {Object} params.editorInstance - Editor instance
 * @param {Array} params.formStructure - Form structure
 * @param {Array} params.selectedGameIds - Array of selected game IDs in order
 * @param {Array} params.gameOptions - Array of available game data objects
 * @param {Object} params.teamsMap - Map of teams by ID
 * @param {Object} params.tournamentOptions - Tournament options
 * @param {Array} params.datafeedData - Datafeed data array
 * @returns {Promise<void>}
 */
export const populateMultipleGameGroupsAsync = async ({
  editorInstance,
  formStructure,
  selectedGameIds,
  gameOptions,
  teamsMap,
  tournamentOptions,
  datafeedData,
}) => {
  if (!editorInstance?.canvas || !selectedGameIds.length) return;

  // Find all game groups on canvas
  const gameGroups = findGameGroups(editorInstance.canvas);

  if (gameGroups.length === 0) {
    console.warn("⚠️ [populateMultipleGameGroupsAsync] No game groups found on canvas");
    return;
  }

  // Sort groups by top position (top to bottom)
  const sortedGroups = [...gameGroups].sort((a, b) => {
    const topA = a.object.top;
    const topB = b.object.top;
    return topA - topB;
  });

  // Populate each group with corresponding game (by selection order)
  const populationPromises = selectedGameIds.map(async (gameId, index) => {
    if (index >= sortedGroups.length) {
      console.warn(`⚠️ [populateMultipleGameGroupsAsync] No group available for game at index ${index}`);
      return;
    }

    const groupPath = sortedGroups[index].path;
    const matchSchedule = gameOptions.find((g) => g.id === gameId);

    const gameData = constructGameData({
      matchSchedule,
      teamsMap,
      tournamentOptions,
      datafeedData,
    });

    if (gameData) {
      await populateGameGroupWithDataAsync(editorInstance, formStructure, groupPath, gameData);
    } else {
      console.warn(`⚠️ [populateMultipleGameGroupsAsync] Game data not found for ID: ${gameId}`);
    }
  });

  await Promise.all(populationPromises);
};
